import type { ImageCategory } from '../constants.js';
import type { OcrConfidence } from '../types.js';

/**
 * Strain draft fields used by guided multi-photo review (Phase 3).
 * Independent of React; OCR field kinds map into this set.
 */
export const DRAFT_FIELDS = [
  'name',
  'productName',
  'producer',
  'brand',
  'category',
  'cultivarType',
  'thcPercent',
  'thcMgPerGram',
  'cbdPercent',
  'cbdMgPerGram',
  'totalTerpenesPercent',
  'netWeightGrams',
  'lotNumber',
  'packageDate',
  'expiryDate',
  'notes',
] as const;

export type DraftField = (typeof DRAFT_FIELDS)[number];

export type AggregatedFieldStatus =
  | 'missing'
  | 'suggested'
  | 'conflict'
  | 'confirmed'
  | 'manual';

export type DraftReviewStatus =
  | 'not_started'
  | 'in_progress'
  | 'ready_for_phase4'
  | 'blocked';

export interface FieldCandidate {
  id: string;
  field: DraftField;
  value: string | number;
  normalizedValue?: string | number;
  rawSourceText?: string;
  /** Engine confidence 0–100 when available. */
  confidence?: number | null;
  confidenceCategory: OcrConfidence;
  sourceImageLocalId: string;
  sourceImageCategory: ImageCategory;
  sourceVariant?: string;
  warnings: string[];
  /** Human-readable ranking reason (set during scoring). */
  scoreReason?: string;
  /** Internal score for deterministic ranking (higher wins). */
  score?: number;
}

export interface AggregatedField {
  field: DraftField;
  candidates: FieldCandidate[];
  recommendedCandidateId?: string;
  recommendedReason?: string;
  selectedCandidateId?: string;
  manualValue?: string | number;
  status: AggregatedFieldStatus;
  /** True when values disagree materially (blocking until resolved). */
  blockingConflict: boolean;
  warnings: string[];
}

export interface FieldSourceRef {
  localId: string;
  category: ImageCategory;
  candidateId?: string;
}

/**
 * Serializable review slice of a package draft (no File/Blob).
 */
export interface PackageDraftReview {
  fields: AggregatedField[];
  selectedCandidateIds: Partial<Record<DraftField, string>>;
  manualValues: Partial<Record<DraftField, string | number>>;
  confirmedFields: DraftField[];
  fieldSources: Partial<Record<DraftField, FieldSourceRef>>;
  conflicts: DraftField[];
  reviewStatus: DraftReviewStatus;
  /** User chose to continue to review without waiting for OCR. */
  explicitManualEntry: boolean;
}

/** Minimal image input for aggregation (no blobs). */
export interface AggregationImageInput {
  localId: string;
  category: ImageCategory;
  sortOrder: number;
  engineConfidence?: number | null;
  variantsRun?: string[];
  candidates: AggregationOcrCandidateInput[];
}

export interface AggregationOcrCandidateInput {
  field: string;
  value: string | number;
  unit?: string;
  sourceText?: string;
  confidence: OcrConfidence;
  percentEquivalent?: number;
}

/** Prior user choices preserved across re-aggregation / rescan. */
export interface AggregationPriorState {
  selectedCandidateIds?: Partial<Record<DraftField, string>>;
  manualValues?: Partial<Record<DraftField, string | number>>;
  confirmedFields?: DraftField[];
  explicitManualEntry?: boolean;
  /** Candidate ids that existed before (for disappearance detection). */
  previousCandidateIdsByField?: Partial<Record<DraftField, string[]>>;
}
